import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { uploadImageService } from "redux/services/general_service";

const PrivacyPolicyModal = ({ isOpen, onClose, policy, onSave }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const quillRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
  });

  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (policy) {
      setFormData({
        title: policy.title || "",
        content: policy.content || "",
      });

      // ✅ إذا في HTML مسبق، نحطو داخل المحرر
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();
          editor.setContents(editor.clipboard.convert(policy.content || ""));
        }
      }, 0);
    } else {
      setFormData({ title: "", content: "" });
    }
  }, [policy, isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // رفع الصور داخل المحرر
  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formDataImage = new FormData();
      formDataImage.append("type", "return_policy");
      formDataImage.append("image", file);

      try {
        setUploading(true);
        const res = await dispatch(uploadImageService(formDataImage)).unwrap();
        if (res?.status !== 200) throw new Error("Upload failed");

        const imageUrl = res?.data?.data?.url;
        if (!imageUrl) throw new Error("No image URL returned");

        // ✅ إدخال الصورة داخل المحرر
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection() || { index: editor.getLength() };
        editor.insertEmbed(range.index, "image", imageUrl);
        editor.setSelection(range.index + 1);
      } catch (err) {
        console.error(err);
        alert("فشل رفع الصورة");
      } finally {
        setUploading(false);
      }
    };
  };

  // تثبيت modules
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: handleImageUpload,
        },
      },
    }),
    []
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const handleSave = () => {
    if (!formData.title || !formData.content || uploading) return;

    // أي HTML + الصور جاهزة في content
    onSave({
      ...policy,
      title: formData.title,
      content: formData.content,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            التعديل على الشروط والأحكام
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1 flex flex-col">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              العنوان
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={formData.title}
              readOnly
            />
          </div>

          {/* Editor */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              المحتوى
            </label>

            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formData.content}
              onChange={(value) => handleChange("content", value)}
              modules={modules}
              formats={formats}
              style={{ height: "300px", marginBottom: "20px" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
          <Button onClick={handleSave} disabled={uploading}>
            {uploading ? "جارِ رفع الصورة..." : t("save")}
          </Button>

          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm"
          >
            {t("cancel")}
          </button>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPolicyModal;